/**
 * User Management API Service
 * This service handles communication with the Azure Functions backend for user management.
 */

import axios from 'axios';

class UserService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE || 'http://localhost:7071/api';
    // Ensure baseURL doesn't end with slash to avoid double slashes
    this.baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    console.log('UserService initialized with baseURL:', this.baseURL);
  }

  /**
   * Create a new user
   * @param {Object} userData - User data to create
   * @returns {Promise} - API response with created user
   */
  async createUser(userData) {
    try {
      const url = `${this.baseURL}/user/post`;
      console.log(`Creating user at: ${url}`);
      
      const response = await axios.post(url, userData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('User created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get all users or a specific user by ID
   * @param {string} userId - Optional user document ID
   * @returns {Promise} - API response with user(s)
   */
  async getUsers(userId = null) {
    try {
      const params = userId ? `?id=${userId}` : '';
      const url = `${this.baseURL}/user/get${params}`;
      console.log(`Getting users from: ${url}`);
      
      const response = await axios.get(url);
      console.log('Users retrieved successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting users:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update a user by ID
   * @param {string} userId - User document ID
   * @param {Object} userData - Updated user data
   * @returns {Promise} - API response with updated user
   */
  async updateUser(userId, userData) {
    try {
      const url = `${this.baseURL}/user/update?id=${userId}`;
      console.log(`Updating user at: ${url}`);
      
      const response = await axios.put(url, userData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('User updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete a user by ID or all users
   * @param {string} userId - User document ID (optional if deleteAll is true)
   * @param {boolean} deleteAll - Whether to delete all users
   * @returns {Promise} - API response
   */
  async deleteUser(userId = null, deleteAll = false) {
    try {
      let url = `${this.baseURL}/user/delete`;
      
      if (deleteAll) {
        url += '?deleteAll=true';
      } else if (userId) {
        url += `?id=${userId}`;
      } else {
        throw new Error('Either userId or deleteAll must be provided');
      }
      
      console.log(`Deleting user(s) at: ${url}`);
      
      const response = await axios.delete(url);
      console.log('User(s) deleted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting user(s):', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get user by doTERRA ID
   * @param {string} doterraId - doTERRA ID to search for
   * @returns {Promise} - API response with user
   */
  async getUserByDoterraId(doterraId) {
    try {
      const allUsers = await this.getUsers();
      const user = allUsers.users?.find(user => 
        user.mentorDoterraId === doterraId || user.doterraId === doterraId
      );
      
      return user || null;
    } catch (error) {
      console.error('Error getting user by doTERRA ID:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Search users by various criteria
   * @param {Object} searchCriteria - Search parameters
   * @returns {Promise} - API response with filtered users
   */
  async searchUsers(searchCriteria) {
    try {
      const allUsers = await this.getUsers();
      let filteredUsers = allUsers.users || [];

      // Apply filters
      if (searchCriteria.email) {
        filteredUsers = filteredUsers.filter(user => 
          user.email.toLowerCase().includes(searchCriteria.email.toLowerCase())
        );
      }

      if (searchCriteria.firstName) {
        filteredUsers = filteredUsers.filter(user => 
          user.firstName.toLowerCase().includes(searchCriteria.firstName.toLowerCase())
        );
      }

      if (searchCriteria.lastName) {
        filteredUsers = filteredUsers.filter(user => 
          user.lastName.toLowerCase().includes(searchCriteria.lastName.toLowerCase())
        );
      }

      if (searchCriteria.doterraId) {
        filteredUsers = filteredUsers.filter(user => 
          user.doterraId?.includes(searchCriteria.doterraId)
        );
      }

      if (searchCriteria.mentorDoterraId) {
        filteredUsers = filteredUsers.filter(user => 
          user.mentorDoterraId?.includes(searchCriteria.mentorDoterraId)
        );
      }

      return {
        users: filteredUsers,
        count: filteredUsers.length
      };
    } catch (error) {
      console.error('Error searching users:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Send bulk email to multiple users using Sendgrid
   * @param {Array} users - Array of user objects with email, firstName, etc.
   * @param {string} subject - Email subject
   * @param {Object} templateData - Template data for personalization
   * @param {string} templateType - Type of template (welcome, promotion, notification, custom)
   * @returns {Promise} - API response
   */
  async sendBulkEmail(users, subject, templateData = {}, templateType = 'custom') {
    try {
      const url = `${this.baseURL}/emailSEND`;
      console.log(`Sending bulk email to ${users.length} users at: ${url}`);
      
      // Prepare recipients data
      const recipients = users.map(user => ({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        name: `${user.firstName} ${user.lastName}`.trim()
      }));

      const requestData = {
        recipients,
        subject,
        templateData,
        templateType
      };

      const response = await axios.post(url, requestData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Bulk email sent successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error sending bulk email:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Send welcome email to new user
   * @param {Object} user - User object
   * @returns {Promise} - API response
   */
  async sendWelcomeEmail(user) {
    try {
      return await this.sendBulkEmail(
        [user],
        'Bun venit la theOilsBabe!',
        {
          firstName: user.firstName,
          companyName: 'theOilsBabe'
        },
        'welcome'
      );
    } catch (error) {
      console.error('Error sending welcome email:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Send promotion email to users
   * @param {Array} users - Array of user objects
   * @param {Object} promotionData - Promotion details
   * @returns {Promise} - API response
   */
  async sendPromotionEmail(users, promotionData) {
    try {
      return await this.sendBulkEmail(
        users,
        promotionData.subject || 'Ofertă specială pentru tine!',
        {
          promotionText: promotionData.text,
          promotionUrl: promotionData.url,
          expirationDate: promotionData.expirationDate
        },
        'promotion'
      );
    } catch (error) {
      console.error('Error sending promotion email:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Send notification email to users
   * @param {Array} users - Array of user objects
   * @param {Object} notificationData - Notification details
   * @returns {Promise} - API response
   */
  async sendNotificationEmail(users, notificationData) {
    try {
      return await this.sendBulkEmail(
        users,
        notificationData.subject || 'Notificare importantă',
        {
          notificationText: notificationData.text,
          notificationDetails: notificationData.details,
          actionUrl: notificationData.actionUrl
        },
        'notification'
      );
    } catch (error) {
      console.error('Error sending notification email:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Send a custom email to selected users
   * @param {Array} users - Array of user objects
   * @param {string} subject - Email subject
   * @param {string} content - Email content
   * @returns {Promise} - API response
   */
  async sendCustomEmail(users, subject, content) {
    try {
      return await this.sendBulkEmail(
        users,
        subject,
        {
          customContent: content
        },
        'custom'
      );
    } catch (error) {
      console.error('Error sending custom email:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Create a rank promotion request
   * @param {Object} requestData - Promotion request data
   * @returns {Promise} - API response
   */
  async createPromotionRequest(requestData) {
    try {
      const url = `${this.baseURL}/rankRequest/post`;
      console.log(`Creating promotion request at: ${url}`);
      
      const response = await axios.post(url, requestData, {
        headers: {
          'Authorization': `Basic ${process.env.REACT_APP_TOKEN}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Promotion request created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating promotion request:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update user settings
   * @param {string} userId - User ID
   * @param {Object} settings - Settings data to update
   * @returns {Promise} - API response
   */
  async updateUserSettings(userId, settings) {
    try {
      const url = `${this.baseURL}/settingsUPDATE`;
      console.log(`Updating user settings at: ${url}`);
      
      const response = await axios.put(url, {
        userId,
        settings
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('User settings updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating user settings:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get user settings
   * @param {string} userId - User ID
   * @returns {Promise} - API response with user settings
   */
  async getUserSettings(userId) {
    try {
      const url = `${this.baseURL}/settingsGET?userId=${userId}`;
      console.log(`Getting user settings from: ${url}`);
      
      const response = await axios.get(url);
      console.log('User settings retrieved successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting user settings:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update user profile
   * @param {string} userId - User ID
   * @param {Object} profileData - Updated profile data
   * @returns {Promise} - API response with updated user
   */
  async updateProfile(userId, profileData) {
    try {
      // Validate inputs
      if (!userId || typeof userId !== 'string') {
        throw new Error('Invalid userId provided');
      }
      
      if (!profileData || typeof profileData !== 'object') {
        throw new Error('Invalid profileData provided');
      }

      // Ensure we don't duplicate /api in the URL path
      const baseUrl = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
      const url = `${baseUrl}/user/update?id=${userId}`;
      console.log(`Updating user profile at: ${url}`);
      console.log('Profile data:', profileData);
      
      const response = await axios.put(url, profileData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('User profile updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   * @param {Error} error - The error object
   * @returns {Error} - Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || error.response.data || 'Server error';
      return new Error(`API Error: ${message}`);
    } else if (error.request) {
      // Request was made but no response received
      return new Error('Network error: No response from server');
    } else {
      // Something else happened
      return new Error(`Request error: ${error.message}`);
    }
  }
}

// Export a singleton instance
const userService = new UserService();
export default userService;
